package com.evoapps.evoshift

import kotlinx.serialization.Serializable

/**
 * Definicja wszystkich ekranów w aplikacji.
 * Dzięki adnotacji @Serializable, Navigation Compose może ich używać bezpiecznie typowo.
 */
@Serializable
sealed interface Route {

    // --- Sekcja Autoryzacji ---
    @Serializable
    data object Login : Route

    // --- Sekcja Główna Aplikacji ---
    @Serializable
    data object Dashboard : Route

    @Serializable
    data object Profile : Route

    @Serializable
    data object CreateWorkplace : Route

    @Serializable
    /** [year] / [month]: preset (SH-89: "Create roster" on the top bar's month without one). */
    data class CreateRoster(val workplaceId: String, val workplaceName: String, val year: Int? = null, val month: Int? = null) : Route

    // --- Workplace Specific (Require Arguments) ---
    @Serializable
    data object Team : Route

    @Serializable
    data object RosterManagement : Route

    @Serializable
    data object Availability : Route

    @Serializable
    data class Schedule(val showMarketplace: Boolean = false) : Route

    @Serializable
    data class AvailabilityForm(val rosterId: String) : Route

    @Serializable
    data class WorkplaceSettings(val workplaceId: String) : Route

    // Future expansion

    /** [openShiftTemplates]: SH-219, with the shift templates' sheet (and its presets) open. */
    @Serializable
    data class SkeletonEditor(val workplaceId: String, val openShiftTemplates: Boolean = false) : Route

    /**
     * [day]: "YYYY-MM-DD" to open with that day's shifts (e.g. from the dashboards' "Today at work").
     * [action]: SH-219, what to open first (ui.roster.RosterDetailAction: auto-assign, the e-mail).
     */
    @Serializable
    data class RosterDetail(val rosterId: String, val day: String? = null, val action: String? = null) : Route

    @Serializable
    /**
     * [applicantPostId] / [applicantId]: SH-125 "Hire": a new person filled in from that applicant,
     * whose application is marked hired once the person is created.
     */
    data class EmployeeForm(
        val workplaceId: String,
        val employeeId: String? = null,
        val applicantPostId: String? = null,
        val applicantId: String? = null
    ) : Route

    /** SH-217: the roster builder's "Import the roster from a spreadsheet" into the month [rosterId]. */
    @Serializable
    data class RosterImport(val rosterId: String) : Route

    /** SH-214: Team → "Import from a spreadsheet" for [workplaceId]. */
    @Serializable
    data class StaffImport(val workplaceId: String) : Route

    @Serializable
    data class RosterSkeleton(val rosterId: String) : Route

    @Serializable
    data class JoinTeam(val code: String) : Route

    /**
     * SH-150: a voice assistant's request ([com.evoapps.evoshift.voice.VoiceAction.path]), opened
     * by links only. [request] tells two requests apart: the same words said again ask again.
     */
    @Serializable
    data class VoiceAction(val action: String, val request: Long = 0) : Route

    @Serializable
    data class WeeklyTemplates(val editTemplateId: String? = null, val openNew: Boolean = false) :
        Route

    @Serializable
    data object RecurringCosts : Route

    /** SH-239: "Recommend EVoShift to another employer" (More, and the card after joining a team). */
    @Serializable
    data object Recommend : Route

    /** SH-124: tips: one's own share; managers also the pools (More → Tips). */
    @Serializable
    data object Tips : Route
    /**
     * SH-131: the owner's shared costs of the business; [convertRuleId] opens the editor filled in
     * from that per-workplace cost rule ("convert to a shared cost").
     */
    @Serializable
    data class SharedCosts(val convertRuleId: String? = null) : Route

    /** SH-267: the owner's workplace groups (who may borrow people from whom), from the profile. */
    @Serializable
    data object WorkplaceGroups : Route

    /** SH-270: the owner's roles (built-in and their own), from the profile. */
    @Serializable
    data object Roles : Route

    /** SH-270: one person's access at a workplace (role and modifications), from their card. */
    @Serializable
    data class PersonAccess(val workplaceId: String, val userId: String) : Route

    /** SH-269: a workplace group's settings, which its workplaces follow by section (the owner's). */
    @Serializable
    data class GroupSettings(val groupId: String) : Route

    /** SH-113: the team's annual leave balances (from the Team screen). */
    @Serializable
    data object LeaveBalances : Route

    /** First run (SH-38): welcome, role, business and workplace or invitation, plan, done. */
    @Serializable
    data object Onboarding : Route

    @Serializable
    data object Financials : Route

    @Serializable
    data object Absences : Route

    @Serializable
    data object Notifications : Route

    /** Which notifications are pushed (SH-73), from the profile. */
    @Serializable
    data object NotificationSettings : Route

    @Serializable
    data object Requests : Route

    @Serializable
    /** [entryDate] ("YYYY-MM-DD"): SH-170, the takings entry opens on that day at once. */
    data class DailyRecords(val workplaceId: String, val rosterId: String, val entryDate: String? = null) : Route

    @Serializable
    data class Payroll(val rosterId: String) : Route

    /** SH-228: the sales statistics of a workplace, from the Money screen's bar; [anchor] ("YYYY-MM-DD"): the month it opens on. */
    @Serializable
    data class Statistics(val workplaceId: String, val anchor: String? = null) : Route

    /** SH-257: "Godziny pracy", confirming the actual hours day by day; [date] ("YYYY-MM-DD"): the day it opens on (null: today). */
    @Serializable
    data class WorkHours(val workplaceId: String, val date: String? = null) : Route

    /** Capacity before planning a month (SH-48): demand, availability, targets, who has not answered. */
    @Serializable
    data class RosterCapacity(val rosterId: String) : Route

    /** The plans (SH-38); owners only. */
    @Serializable
    data object Paywall : Route

    /** The owner's plan and subscription (SH-38), from the profile. */
    @Serializable
    data object Subscription : Route

    /** Team announcements (SH-118): the managers' posts; managers also post and see read receipts. */
    @Serializable
    data object Announcements : Route
    /**
     * SH-121: the workplace's checklists: the day's (who ticked what, [date] "YYYY-MM-DD", today when
     * null) and the templates. [workplaceId]: another workplace than the session's (a notification).
     */
    @Serializable
    data class Checklists(val workplaceId: String? = null, val date: String? = null) : Route

    /** SH-121: the checklists of one of the person's shifts, to tick. */
    @Serializable
    data class ShiftChecklist(val shiftId: String) : Route
    /**
     * SH-122: [userId]'s documents at [workplaceId] (contract, medical certificate, OHS, other):
     * the person themselves ([userId] null: me, from the profile) or the workplace's owner and
     * financial managers (employee form).
     */
    @Serializable
    data class StaffDocuments(val workplaceId: String, val userId: String? = null) : Route

    /**
     * SH-212: [userId]'s timeline at [workplaceId] (hire, contract, pay, statuses, OHS, notes with
     * the days they apply from): the workplace's owner and financial managers (employee form), or
     * the person themselves read-only ([userId] null: me, from the profile).
     */
    @Serializable
    data class EmployeeTimeline(val workplaceId: String, val userId: String? = null) : Route

    /** SH-122: the workplace's policies to read and confirm; managers publish them and see who confirmed. */
    @Serializable
    data class Policies(val workplaceId: String) : Route

    /**
     * SH-126: training at [workplaceId]: the person's courses and OHS record; for managers also the
     * courses, the team's training status and their records.
     */
    @Serializable
    data class Courses(val workplaceId: String) : Route

    /** SH-126: writing a course (managers); [courseId] null: a new one. */
    @Serializable
    data class CourseEditor(val workplaceId: String, val courseId: String? = null) : Route

    /** SH-126: taking a course: its pages, the quiz and the result. */
    @Serializable
    data class TakeCourse(val workplaceId: String, val courseId: String) : Route

    /** SH-126: who a course is for and how far each person got (managers). */
    @Serializable
    data class CourseProgress(val workplaceId: String, val courseId: String) : Route

    /** SH-126: [userId]'s OHS training record ([userId] null: mine). */
    @Serializable
    data class OhsTraining(val workplaceId: String, val userId: String? = null) : Route
    /** SH-123: takings from a sales file or the till (More → Sales import); owners and financial managers. */
    @Serializable
    data object SalesImport : Route
    /** SH-125: the workplace's job posts (Team → Hiring). */
    @Serializable
    data object Hiring : Route

    /** SH-125: a job post's editor with its share link and QR code; [postId] null: a new post. */
    @Serializable
    data class JobPostEditor(val postId: String? = null) : Route

    /** SH-125: the applicants of a job post (the pipeline). */
    @Serializable
    data class Applicants(val postId: String) : Route

    /** The More tab (SH-42): sections without a tab of their own, the workplace, the account. */
    @Serializable
    data object More : Route
}
